import {
  AnalyticsPageEmployeeNamesFragment,
  AnalyticsPageLegalEntitiesFragment,
  AnalyticsPageLocationsFragment,
} from 'generated/resources/graphql'
import { xor } from 'lodash'

import type { GroupByType } from 'modules/performance_analytics/components/helpers'
import { TeamsTeam } from 'modules/performance_analytics/hooks/useGroupByKeysLookupTransformer'

import { mapDisplayNameToRawValue } from './mappers'

interface TransformationContext {
  groupBy: GroupByType | null
  legalEntities?: AnalyticsPageLegalEntitiesFragment[]
  teams?: Pick<TeamsTeam, 'id' | 'name'>[]
  locations?: AnalyticsPageLocationsFragment[]
  employeesNames?: AnalyticsPageEmployeeNamesFragment[]
}

/**
 * Handles special logic for manager groupBy field
 */
class ManagerValueHandler {
  private employeesNames: AnalyticsPageEmployeeNamesFragment[]

  constructor(employeesNames: AnalyticsPageEmployeeNamesFragment[] = []) {
    this.employeesNames = employeesNames
  }

  /**
   * Converts manager display name to employee object for filter values
   */
  transformDisplayNameToFilterValue(
    displayName: string | undefined
  ): AnalyticsPageEmployeeNamesFragment | undefined {
    const result = mapDisplayNameToRawValue(
      displayName,
      'manager',
      undefined,
      undefined,
      undefined,
      this.employeesNames
    )
    return result && typeof result === 'object' ? result : undefined
  }

  /**
   * Processes manager filter values to employee objects
   */
  processFilterValues(values: unknown[]): AnalyticsPageEmployeeNamesFragment[] {
    return values
      .map((value) => this.employeesNames.find((employee) => employee.id === value))
      .filter((employee): employee is AnalyticsPageEmployeeNamesFragment => Boolean(employee))
  }

  /**
   * Gets raw value (employee ID) from employee object
   */
  getRawValue(employee: AnalyticsPageEmployeeNamesFragment | undefined): number | undefined {
    return employee?.id
  }
}

/**
 * Generic value transformer for all other groupBy types
 */
const transformGenericValue = (
  displayName: string | undefined,
  context: TransformationContext
): string | number | AnalyticsPageEmployeeNamesFragment | undefined => {
  return mapDisplayNameToRawValue(
    displayName,
    context.groupBy,
    context.legalEntities,
    context.teams,
    context.locations,
    context.employeesNames
  )
}

/**
 * Main transformer that handles all groupBy types
 */
export const createFilterValueTransformer = (context: TransformationContext) => {
  const isManagerGroupBy = context.groupBy === 'manager'
  const managerHandler = isManagerGroupBy ? new ManagerValueHandler(context.employeesNames) : null

  const transformToRawValue = (
    displayName: string | undefined
  ): string | number | AnalyticsPageEmployeeNamesFragment | undefined => {
    if (isManagerGroupBy && managerHandler) {
      const employee = managerHandler.transformDisplayNameToFilterValue(displayName)
      return managerHandler.getRawValue(employee)
    }
    return transformGenericValue(displayName, context)
  }

  const processFilterValues = (values: unknown[]): unknown[] => {
    if (isManagerGroupBy && managerHandler) {
      return managerHandler.processFilterValues(values)
    }
    return values
  }

  return {
    /**
     * Converts display name to raw filter value
     */
    transformToRawValue,

    /**
     * Processes filter values after XOR operation
     */
    processFilterValues,

    /**
     * Complete transformation pipeline for click handling
     */
    handleClickTransformation: (displayName: string, currentValues: unknown[]): unknown[] => {
      const rawValue = transformToRawValue(displayName)
      const toggledValues = xor(currentValues, [rawValue])
      return processFilterValues(toggledValues)
    },
  }
}
